import { useState, useMemo } from 'react';
import { trpc } from '../lib/trpc.js';
import { formatGbp } from '../lib/format.js';
import { clsx } from 'clsx';
import { format, subMonths, startOfYear } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';

const PRESETS = [
  { label: '30d', from: () => format(subMonths(new Date(), 1), 'yyyy-MM-dd') },
  { label: '90d', from: () => format(subMonths(new Date(), 3), 'yyyy-MM-dd') },
  { label: '6m',  from: () => format(subMonths(new Date(), 6), 'yyyy-MM-dd') },
  { label: 'YTD', from: () => format(startOfYear(new Date()), 'yyyy-MM-dd') },
  { label: 'All', from: () => '2026-01-01' },
];

export function PayoutsPage() {
  const [from, setFrom] = useState<string>(PRESETS[1]!.from());
  const [techFilter, setTechFilter] = useState<string>('');
  const list = trpc.payouts.list.useQuery({ from, technicianName: techFilter || undefined, limit: 200 });
  type PayoutItem = NonNullable<typeof list.data>['items'][number];

  const grouped = useMemo(() => {
    const m = new Map<string, PayoutItem[]>();
    if (!list.data) return m;
    for (const p of list.data.items) {
      const key = format(new Date(p.weekStart), 'yyyy-MM-dd');
      const existing = m.get(key);
      if (existing) existing.push(p);
      else m.set(key, [p]);
    }
    return m;
  }, [list.data]);

  if (list.isLoading) return <p className="text-gray-500">Loading…</p>;
  if (!list.data) return <p className="text-gray-500">No data</p>;

  const totals = list.data.totals;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-3xl text-navy">Payouts</h1>
        <p className="text-sm text-gray-500">All payslips issued to technicians, grouped by paid week.</p>
      </div>

      <div className="card">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs uppercase tracking-wider text-gray-500">From</span>
          {PRESETS.map((p) => (
            <button
              key={p.label}
              onClick={() => setFrom(p.from())}
              className={clsx(
                'px-3 py-1.5 rounded-md text-xs font-medium border',
                from === p.from() ? 'bg-navy text-white border-navy' : 'bg-white text-navy border-gray-200',
              )}
            >
              {p.label}
            </button>
          ))}
          <input
            type="date"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
            className="px-2 py-1 rounded border border-gray-200 text-sm"
          />
          <span className="ml-4 text-xs uppercase tracking-wider text-gray-500">Tech</span>
          <input
            type="text"
            value={techFilter}
            onChange={(e) => setTechFilter(e.target.value)}
            placeholder="(all)"
            className="px-2 py-1 rounded border border-gray-200 text-sm w-48"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Tile label="Total paid"   value={formatGbp(totals.totalAmount)} accent="green" />
        <Tile label="Total hours"  value={`${totals.totalHours.toFixed(1)}h`} accent="navy" />
        <Tile label="Sent"         value={String(totals.countSent)} accent="green" />
        <Tile label="Failed"       value={String(totals.countFailed)} accent="red" />
        <Tile label="Manual / no email" value={String(totals.countManual)} accent="amber" />
      </div>

      {list.data.items.length === 0 ? (
        <div className="card text-sm text-gray-500">No payouts in this range yet. Issue payslips from /timesheets.</div>
      ) : (
        Array.from(grouped.entries()).sort((a, b) => b[0].localeCompare(a[0])).map(([weekKey, items]) => {
          const weekTotal = items.reduce((s: number, p: PayoutItem) => s + Number(p.totalAmount), 0);
          return (
            <section key={weekKey} className="card">
              <div className="flex items-baseline justify-between mb-3">
                <h2 className="font-display text-lg text-navy">
                  Week of {formatInTimeZone(new Date(weekKey), 'Europe/London', 'd MMM yyyy')}
                </h2>
                <span className="text-sm font-mono text-gray-500">
                  {items.length} payout{items.length === 1 ? '' : 's'} · <span className="text-navy font-medium">{formatGbp(weekTotal)}</span>
                </span>
              </div>
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-100 text-xs text-gray-500">
                    <th className="text-left py-2 px-2 font-medium">Technician</th>
                    <th className="text-right py-2 px-2 font-medium">Hours</th>
                    <th className="text-right py-2 px-2 font-medium">Rate</th>
                    <th className="text-right py-2 px-2 font-medium">Amount</th>
                    <th className="text-left py-2 px-2 font-medium">Sent to</th>
                    <th className="text-left py-2 px-2 font-medium">Status</th>
                    <th className="text-left py-2 px-2 font-medium">When</th>
                    <th className="text-center py-2 px-2 font-medium"></th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((p: PayoutItem) => (
                    <tr key={p.id} className="border-b border-gray-50 hover:bg-cream-tint">
                      <td className="py-1.5 px-2 font-medium text-navy">{p.technicianName}</td>
                      <td className="py-1.5 px-2 text-right font-mono">{Number(p.totalHours).toFixed(2)}</td>
                      <td className="py-1.5 px-2 text-right font-mono">£{Number(p.hourlyRate).toFixed(2)}</td>
                      <td className="py-1.5 px-2 text-right font-mono text-navy">{formatGbp(Number(p.totalAmount))}</td>
                      <td className="py-1.5 px-2 text-xs text-gray-500">{p.sentToEmail ?? '—'}</td>
                      <td className="py-1.5 px-2">
                        <span className={clsx(
                          'text-xs px-2 py-0.5 rounded font-medium',
                          p.status === 'sent' && 'bg-green-100 text-green-700',
                          p.status === 'failed' && 'bg-red-100 text-red-700',
                          p.status === 'manual' && 'bg-amber-100 text-amber-700',
                        )}>
                          {p.status}
                        </span>
                        {p.errorMessage && <span className="ml-2 text-xs text-gray-500" title={p.errorMessage}>ⓘ</span>}
                      </td>
                      <td className="py-1.5 px-2 text-xs text-gray-500">
                        {formatInTimeZone(new Date(p.sentAt), 'Europe/London', 'd MMM HH:mm')}
                      </td>
                      <td className="py-1.5 px-2 text-center">
                        <a
                          href={`/payslip?tech=${encodeURIComponent(p.technicianName)}&weekStart=${format(new Date(p.weekStart), 'yyyy-MM-dd')}`}
                          target="_blank"
                          rel="noreferrer"
                          className="text-xs text-navy underline"
                        >
                          View
                        </a>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
          );
        })
      )}
    </div>
  );
}

function Tile({ label, value, accent }: { label: string; value: string; accent: 'green' | 'navy' | 'red' | 'amber' }) {
  const border = {
    green: 'border-l-green-500',
    navy: 'border-l-navy',
    red: 'border-l-red-500',
    amber: 'border-l-amber-500',
  }[accent];
  return (
    <div className={clsx('card border-l-4', border)}>
      <p className="text-xs text-gray-500 mb-1">{label}</p>
      <p className="text-xl font-display text-navy">{value}</p>
    </div>
  );
}
